/**
 * Filter Bar Component
 * Top filter bar for kanban board
 */
import { useState } from 'react'
import { Search, Filter, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { ActionFilters } from '@/types/actions'
interface FilterBarProps {
  filters: ActionFilters
  onFiltersChange: (filters: ActionFilters) => void
}

export function FilterBar({ filters, onFiltersChange }: FilterBarProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '')
  
  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    // Debounce search
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: value || undefined })
    }, 300)
    return () => clearTimeout(timer)
  }
  
  const toggleFilter = (key: keyof ActionFilters, value: boolean) => {
    onFiltersChange({ ...filters, [key]: value || undefined })
  }
  
  const clearFilters = () => {
    setSearchValue('')
    onFiltersChange({})
  }
  
  const activeFilterCount = Object.keys(filters).filter(
    (key) => filters[key as keyof ActionFilters] !== undefined
  ).length
  
  return (
    <div className="border-b bg-background p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search action items..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        
        {/* My Tasks Toggle */}
        <Button
          variant={filters.my_tasks ? 'default' : 'outline'}
          size="sm"
          onClick={() => toggleFilter('my_tasks', !filters.my_tasks)}
        >
          My Tasks
        </Button>
        
        {/* Unassigned Toggle */}
        <Button
          variant={filters.unassigned ? 'default' : 'outline'}
          size="sm"
          onClick={() => toggleFilter('unassigned', !filters.unassigned)}
        >
          Unassigned
        </Button>
        
        {/* More Filters */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-4">
              <h4 className="font-medium">Filter Options</h4>
              
              {/* Priority Filter */}
              <div className="space-y-2">
                <Label>Priority</Label>
                <div className="space-y-2">
                  {['low', 'medium', 'high'].map((priority) => (
                    <div key={priority} className="flex items-center space-x-2">
                      <Checkbox
                        id={`priority-${priority}`}
                        checked={filters.priority?.includes(priority)}
                        onCheckedChange={(checked) => {
                          const current = filters.priority?.split(',').filter(Boolean) || []
                          const updated = checked
                            ? [...current, priority]
                            : current.filter((p) => p !== priority)
                          onFiltersChange({
                            ...filters,
                            priority: updated.length ? updated.join(',') : undefined,
                          })
                        }}
                      />
                      <label
                        htmlFor={`priority-${priority}`}
                        className="text-sm capitalize cursor-pointer"
                      >
                        {priority}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Archived Items */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="archived"
                  checked={filters.archived}
                  onCheckedChange={(checked) =>
                    toggleFilter('archived', checked as boolean)
                  }
                />
                <label htmlFor="archived" className="text-sm cursor-pointer">
                  Show archived items
                </label>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        {/* Clear Filters */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}
